import React, { ReactNode, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AcceptJsToken,
  Cart,
  CartLine,
  PaymentMode,
  PaymentSource,
  ShippingAddress,
  SubmitInput,
  SubmitPayment,
  SubmitResult,
} from "../../types";
import { CartSummary } from "../CartSummary/CartSummary";
import { ShippingForm, isShippingComplete } from "../ShippingForm/ShippingForm";
import { DiscountCodeInput } from "../DiscountCodeInput/DiscountCodeInput";
import { VoucherSelector } from "../VoucherSelector/VoucherSelector";
import { AdminDiscountControls } from "../AdminDiscountControls/AdminDiscountControls";
import { PaymentSourcePicker } from "../PaymentSourcePicker/PaymentSourcePicker";
import {
  InlinePaymentForm,
  InlinePaymentFields,
  isInlineFormComplete,
} from "../InlinePaymentForm/InlinePaymentForm";
import { AddCardPanel } from "../AddCardPanel/AddCardPanel";
import { tokenizeCard } from "../../utils/tokenizeCard";

type AdminDiscountInput = {
  type: "percentage" | "fixed_amount";
  value: number;
  reason: string;
};

export type CheckoutShellProps = {
  cart: Cart;
  paymentMode: PaymentMode;
  paymentSources?: PaymentSource[];

  onApplyDiscountCode?: (code: string) => Promise<Cart>;
  onClearDiscountCode?: () => Promise<Cart>;
  onApplyVouchers?: (count: number) => Promise<Cart>;
  // Hard ceiling on the voucher count, forwarded to VoucherSelector.
  // The domain computes it; the shell just passes it through.
  voucherMaxQuantity?: number;
  onApplyAdminDiscount?: (input: AdminDiscountInput) => Promise<Cart>;
  onClearAdminDiscount?: () => Promise<Cart>;

  // Optional per-line cart-edit callbacks. When BOTH are provided the cart
  // summary renders +/− and remove controls. Used by product checkout
  // where users adjust quantities at the cart-review stage. Events /
  // subscriptions omit these and the cart stays read-only.
  onUpdateLineQuantity?: (line: CartLine, quantity: number) => Promise<Cart>;
  onRemoveLine?: (line: CartLine) => Promise<Cart>;

  onAddPaymentSource?: (token: AcceptJsToken) => Promise<PaymentSource>;
  onSubmit: (input: SubmitInput) => Promise<SubmitResult>;

  slots?: {
    aboveCart?: ReactNode;
    belowCart?: ReactNode;
    beforeSubmit?: ReactNode;
  };
};

const emptyShipping: Partial<ShippingAddress> = {};
const emptyInline: InlinePaymentFields = {
  cardholderName: "",
  cardNumber: "",
  expMonth: "",
  expYear: "",
  cvc: "",
};

export function CheckoutShell(props: CheckoutShellProps) {
  const {
    paymentMode,
    paymentSources: paymentSourcesProp,
    onApplyDiscountCode,
    onClearDiscountCode,
    onApplyVouchers,
    voucherMaxQuantity,
    onApplyAdminDiscount,
    onClearAdminDiscount,
    onUpdateLineQuantity,
    onRemoveLine,
    onAddPaymentSource,
    onSubmit,
    slots,
  } = props;

  const [cart, setCart] = useState<Cart>(props.cart);
  const [localSources, setLocalSources] = useState<PaymentSource[]>(
    paymentSourcesProp ?? [],
  );
  const paymentSources = useMemo(() => localSources, [localSources]);

  const [shipping, setShipping] =
    useState<Partial<ShippingAddress>>(emptyShipping);
  const [inlineFields, setInlineFields] =
    useState<InlinePaymentFields>(emptyInline);
  const [selectedSourceId, setSelectedSourceId] = useState<string | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const isFree = cart.total === 0;
  const shippingComplete = !cart.needsShipping || isShippingComplete(shipping);

  const paymentComplete = (() => {
    if (isFree) return true;
    if (paymentMode === "inline") return isInlineFormComplete(inlineFields);
    if (paymentMode === "picker-required") return Boolean(selectedSourceId);
    return false;
  })();

  const canSubmit = !submitting && shippingComplete && paymentComplete;

  const buildPayment = async (): Promise<SubmitPayment | undefined> => {
    if (isFree) return undefined;
    if (paymentMode === "inline") {
      // Tokenize card via Accept.js. Throws on AuthNet decline / misconfig;
      // handleSubmit surfaces the message inline above the submit button.
      const token = await tokenizeCard(inlineFields);
      return { kind: "token", token };
    }
    if (paymentMode === "picker-required" && selectedSourceId) {
      return { kind: "saved", paymentSourceId: selectedSourceId };
    }
    return undefined;
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const payment = await buildPayment();
      // Adapter handles navigation off the returned result; the shell
      // doesn't render a confirmation screen anymore.
      await onSubmit({
        cart,
        payment,
        shipping: cart.needsShipping
          ? (shipping as ShippingAddress)
          : undefined,
      });
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCardAdded = (saved: PaymentSource) => {
    setLocalSources((prev) => [saved, ...prev]);
    setSelectedSourceId(saved.paymentSourceId);
  };

  const submitLabel = isFree
    ? "Confirm"
    : paymentMode === "picker-required"
      ? "Subscribe"
      : `Pay ${formatTotal(cart.total)}`;

  const showLeftColumn =
    cart.needsShipping ||
    (cart.acceptsDiscountCode && Boolean(onApplyDiscountCode)) ||
    (cart.acceptsVouchers && Boolean(onApplyVouchers)) ||
    (cart.adminCanDiscount && Boolean(onApplyAdminDiscount)) ||
    (!isFree && paymentMode === "inline") ||
    (!isFree && paymentMode === "picker-required");

  const submitButton = (
    <>
      <Button
        className="w-full"
        data-testid="submit-button"
        disabled={!canSubmit}
        onClick={handleSubmit}
        type="button"
      >
        {submitting ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Processing…
          </>
        ) : (
          submitLabel
        )}
      </Button>
      {submitError ? (
        <div
          className="mt-3 rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-700 dark:text-red-300"
          data-testid="submit-error"
        >
          {submitError}
        </div>
      ) : null}
    </>
  );

  return (
    <div className="w-full" data-testid="checkout-shell">
      {slots?.aboveCart}

      {/* Always render the 5-col grid so the Order Summary stays
        anchored on the right. When the left column has nothing to
        show (e.g. every attendee is waitlisted, no shipping, no
        discount UI, no payment form), it just stays empty — the
        right-side summary doesn't reflow to center. */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
        <div className="lg:col-span-3 space-y-6">
          {showLeftColumn ? (
            <>
              {(cart.acceptsDiscountCode && onApplyDiscountCode) ||
              (cart.acceptsVouchers && onApplyVouchers) ||
              (cart.adminCanDiscount && onApplyAdminDiscount) ? (
                <div className="space-y-1 pb-6 border-b border-gray-200 dark:border-slate-700">
                  {cart.acceptsVouchers && onApplyVouchers ? (
                    <VoucherSelector
                      maxQuantity={voucherMaxQuantity}
                      onApply={onApplyVouchers}
                      onCartUpdate={setCart}
                    />
                  ) : null}
                  {cart.acceptsDiscountCode && onApplyDiscountCode ? (
                    <DiscountCodeInput
                      onApply={onApplyDiscountCode}
                      onCartUpdate={setCart}
                      onClear={onClearDiscountCode}
                    />
                  ) : null}
                  {cart.adminCanDiscount && onApplyAdminDiscount ? (
                    <AdminDiscountControls
                      onApply={onApplyAdminDiscount}
                      onCartUpdate={setCart}
                      onClear={onClearAdminDiscount}
                    />
                  ) : null}
                </div>
              ) : null}

              {cart.needsShipping ? (
                <ShippingForm onChange={setShipping} value={shipping} />
              ) : null}

              {!isFree && paymentMode === "inline" ? (
                <InlinePaymentForm
                  onChange={setInlineFields}
                  value={inlineFields}
                />
              ) : null}

              {!isFree && paymentMode === "picker-required" ? (
                <>
                  <PaymentSourcePicker
                    onSelect={setSelectedSourceId}
                    paymentSources={paymentSources}
                    selectedId={selectedSourceId}
                  />
                  {onAddPaymentSource ? (
                    <AddCardPanel
                      onAddCard={onAddPaymentSource}
                      onCardAdded={handleCardAdded}
                    />
                  ) : null}
                </>
              ) : null}
            </>
          ) : null}
        </div>

        <div className="lg:col-span-2">
          <div className="lg:sticky lg:top-24">
            <CartSummary
              cart={cart}
              onCartUpdate={setCart}
              onRemoveLine={onRemoveLine}
              onUpdateLineQuantity={onUpdateLineQuantity}
            >
              {slots?.beforeSubmit}
              {submitButton}
            </CartSummary>
          </div>
        </div>
      </div>

      {slots?.belowCart}
    </div>
  );
}

const formatTotal = (n: number): string =>
  `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
